<script lang="ts">
	import { showCookieBanner, acceptAllCookies, acceptNecessaryOnly, getInitialConsent } from '$lib/stores/cookies';
	import Button from '$lib/components/primitives/Button.svelte';
	import CookieSettings from './CookieSettings.svelte';
	import { fade, slide } from 'svelte/transition';
	import { _ } from 'svelte-i18n';
	import { browser } from '$app/environment';
	
	let showDetails = $state(false);
	let showSettings = $state(false);
	
	// Check initial consent state synchronously to prevent flash
	let shouldShowBanner = $state(!getInitialConsent());
	let hasInitialized = $state(false);
	
	// Update state when store changes - directly bind to store
	$effect(() => {
		if (browser) {
			if (!hasInitialized) {
				// Small delay to ensure smooth transition after initial load
				setTimeout(() => {
					hasInitialized = true;
					shouldShowBanner = $showCookieBanner;
				}, 100);
			} else {
				// After initialization, update immediately
				shouldShowBanner = $showCookieBanner;
			}
		}
	});
</script>

{#if shouldShowBanner}
	<div class="cookie-banner" transition:slide={{ duration: hasInitialized ? 300 : 0 }}>
		<div class="cookie-container">
			<div class="cookie-content">
				<div class="cookie-main">
					<div class="cookie-text">
						<h3 class="cookie-title">🍪 {$_('cookies.banner.title')}</h3>
						<p class="cookie-description">
							{$_('cookies.banner.description')}
						</p>
						{#if showDetails}
							<div class="cookie-details" transition:slide={{ duration: hasInitialized ? 200 : 0 }}>
								<button 
									class="details-toggle"
									onclick={() => showDetails = false}
									aria-expanded="true"
								>
									{$_('cookies.banner.hideDetails')}
									<svg class="arrow arrow--up" viewBox="0 0 20 20" fill="currentColor">
										<path fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
									</svg>
								</button>
								<div class="details-content">
									<div class="cookie-type">
										<h4>{$_('cookies.categories.necessary.title')}</h4>
										<p>{$_('cookies.categories.necessary.description')}</p>
									</div>
									<div class="cookie-type">
										<h4>{$_('cookies.categories.analytics.title')}</h4>
										<p>{$_('cookies.categories.analytics.description')}</p>
									</div>
									<div class="cookie-type">
										<h4>{$_('cookies.categories.marketing.title')}</h4>
										<p>{$_('cookies.categories.marketing.description')}</p>
									</div>
									<div class="cookie-type">
										<h4>{$_('cookies.categories.preferences.title')}</h4>
										<p>{$_('cookies.categories.preferences.description')}</p>
									</div>
								</div>
							</div>
						{:else}
							<button 
								class="details-toggle"
								onclick={() => showDetails = true}
								aria-expanded="false"
							>
								{$_('cookies.banner.showDetails')}
								<svg class="arrow" viewBox="0 0 20 20" fill="currentColor">
									<path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
								</svg>
							</button>
						{/if}
					</div>
				</div>
				
				<div class="cookie-actions">
					<Button 
						variant="ghost" 
						size="sm" 
						onclick={() => {
							acceptNecessaryOnly();
							showDetails = false;
						}}
					>
						{$_('cookies.banner.necessaryOnly')}
					</Button>
					<Button 
						variant="ghost" 
						size="sm" 
						onclick={() => showSettings = true}
					>
						{$_('cookies.banner.preferences')}
					</Button>
					<Button 
						variant="primary" 
						size="sm" 
						onclick={() => {
							acceptAllCookies();
							showDetails = false;
						}}
					>
						{$_('cookies.banner.acceptAll')}
					</Button>
				</div>
			</div>
		</div>
	</div>
{/if}

{#if showSettings}
	<!-- Cookie Settings Modal -->
	<div class="cookie-modal" transition:fade={{ duration: 200 }}>
		<button class="modal-overlay" onclick={() => showSettings = false} aria-label={$_('components.cookieBanner.closeSettings')}></button>
		<div class="modal-content" transition:slide={{ duration: 300 }}>
			<CookieSettings onclose={() => showSettings = false} />
		</div>
	</div>
{/if}

<style>
	.cookie-banner {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: var(--z-toast);
		background: var(--bg-elevated);
		border-top: 1px solid var(--border-light);
		box-shadow: var(--shadow-2xl);
		backdrop-filter: blur(var(--backdrop-blur-xl));
	}
	
	.cookie-container {
		max-width: var(--container-max);
		margin-inline: auto;
		padding: var(--space-6) var(--container-padding);
	}
	
	.cookie-content {
		display: flex;
		gap: var(--space-8);
		align-items: flex-start;
		justify-content: space-between;
		
		@media (max-width: 768px) {
			flex-direction: column;
			gap: var(--space-4);
		}
	}
	
	.cookie-main {
		flex: 1;
		max-width: 800px;
	}
	
	.cookie-title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}
	
	.cookie-description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		margin-bottom: var(--space-3);
	}
	
	.details-toggle {
		display: inline-flex;
		align-items: center;
		gap: var(--space-1);
		padding: var(--space-1) var(--space-2);
		background: transparent;
		border: none;
		color: var(--text-brand);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		cursor: pointer;
		transition: var(--transition-colors);
		border-radius: var(--radius-sm);
	}
	
	.details-toggle:hover {
		background: var(--bg-secondary);
	}
	
	.arrow {
		width: 16px;
		height: 16px;
		transition: var(--transition-transform);
	}
	
	.arrow--up {
		transform: rotate(180deg);
	}
	
	.cookie-details {
		margin-top: var(--space-4);
		padding: var(--space-4);
		background: var(--bg-secondary);
		border-radius: var(--radius-md);
		border: 1px solid var(--border-light);
	}
	
	.details-content {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: var(--space-4);
		margin-top: var(--space-3);
	}
	
	.cookie-type h4 {
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-1);
	}
	
	.cookie-type p {
		font-size: var(--font-size-xs);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
	}
	
	.cookie-actions {
		display: flex;
		gap: var(--space-3);
		align-items: center;
		flex-shrink: 0;
		
		@media (max-width: 768px) {
			width: 100%;
			justify-content: stretch;
			
			:global(.btn) {
				flex: 1;
			}
		}
	}
	
	/* Modal Styles */
	.cookie-modal {
		position: fixed;
		inset: 0;
		z-index: var(--z-modal);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--space-6);
	}
	
	.modal-overlay {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.5);
		backdrop-filter: blur(4px);
		border: none;
		cursor: pointer;
	}
	
	.modal-content {
		position: relative;
		width: 100%;
		max-width: 600px;
		max-height: 80vh;
		overflow-y: auto;
		background: var(--bg-primary);
		border-radius: var(--radius-xl);
		box-shadow: var(--shadow-2xl);
	}
</style>